import React from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../layouts/LandingLayout';
import Button from '../components/ui-components/Button';
import { Container } from '../components/layout/Container';
import { 
  ArrowRight, 
  Calendar, 
  Share2, 
  CheckCircle, 
  Star, 
  Users, 
  Clock,
  ChevronRight,
  CalendarCheck,
  Bell,
  Video
} from 'lucide-react';

const Home: React.FC = () => {
  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-16 md:pt-0">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50/50 to-background dark:from-blue-950/20 dark:via-purple-950/10 dark:to-background"></div>
        
        {/* Split Container */}
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 py-10 md:py-20">
            {/* Left Side - Content */}
            <div className="flex flex-col justify-center space-y-6 md:space-y-10 relative z-10">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full px-3 md:px-4 py-1.5 flex items-center gap-2">
                    <span className="h-2 w-2 bg-blue-600 rounded-full animate-pulse"></span>
                    <span className="text-xs md:text-sm font-medium text-blue-600 dark:text-blue-400">Now with AI-powered scheduling</span>
                  </div>
                </div>
                
                <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold tracking-tight text-left">
                  <span className="text-gray-900 dark:text-gray-100">
                    Your Time,
                  </span>
                  <br />
                  <span className="text-blue-600 dark:text-blue-500">
                    Perfectly Planned
                  </span>
                </h1>
                
                <p className="text-base md:text-xl text-gray-600 dark:text-gray-300 max-w-xl leading-relaxed">
                  Stop playing email tag. Our AI-powered scheduling assistant helps you book meetings instantly, 
                  syncs across all calendars, and adapts to your preferences.
                </p>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <Link to="/signup" className="w-full sm:w-auto">
                    <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 transition-all duration-300 shadow-lg text-white px-6 md:px-8 h-12 md:h-14 text-base md:text-lg">
                      Get Started Free
                      <ArrowRight className="h-4 w-4 md:h-5 md:w-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/login" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm md:text-base">
                    Already have an account?
                  </Link>
                </div>

                <div className="flex items-center gap-4 md:gap-6 pt-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white dark:border-gray-800 bg-gray-${i * 100}`}></div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-3 h-3 md:w-4 md:h-4 fill-current text-yellow-400" />
                        ))}
                      </div>
                      <span className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200">4.9/5</span>
                    </div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Trusted by 10,000+ professionals</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Calendar & Notifications */}
            <div className="relative flex items-center justify-center lg:justify-end min-h-[700px]">
              <div className="relative w-[600px] h-[700px]">
                {/* Main Calendar Card */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] bg-white dark:bg-gray-800 rounded-3xl shadow-lg">
                  <div className="p-8">
                    <h3 className="text-2xl font-medium text-gray-900 dark:text-gray-100 mb-8">March</h3>
                    <div className="grid grid-cols-7 gap-6 text-sm mb-6">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="text-center font-medium text-gray-500">{day}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-6">
                      {Array(31).fill(0).map((_, i) => (
                        <div
                          key={i}
                          className={`relative h-10 w-10 flex items-center justify-center text-sm transition-all duration-200
                            ${i === 14 ? 'text-white' : i < 3 ? 'text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}
                        >
                          {i === 14 && (
                            <div className="absolute inset-0 bg-blue-600 rounded-full transform scale-110" />
                          )}
                          <span className="relative z-10">{i + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Team Sync Card - Top Left */}
                <div className="absolute top-4 left-0 w-[280px] bg-white dark:bg-gray-800 rounded-2xl shadow-md">
                  <div className="p-4 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                      <Video className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Team Sync</h4>
                      <p className="text-sm text-gray-500">Weekly Standup</p>
                    </div>
                  </div>
                </div>

                {/* Almost Time Notification - Top Right */}
                <div className="absolute top-20 right-0 w-[320px]">
                  <div className="bg-orange-500 rounded-2xl shadow-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Bell className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-lg">Almost Time!</h4>
                        <p className="text-white/90">Stay close to your phone - meeting in 15 mins</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meeting Confirmed - Middle Right */}
                <div className="absolute top-1/2 right-0 transform -translate-y-1/2 w-[320px]">
                  <div className="bg-emerald-500 rounded-2xl shadow-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-lg">Meeting Confirmed!</h4>
                        <p className="text-white/90">Your appointment is scheduled</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategy Meeting Card - Bottom Left */}
                <div className="absolute bottom-20 left-0 w-[280px] bg-white dark:bg-gray-800 rounded-2xl shadow-md">
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                        <CalendarCheck className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">Strategy Meeting</h4>
                        <p className="text-sm text-gray-500">with John Doe</p>
                      </div>
                    </div>
                    <div className="space-y-2 ml-[60px]">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">Tomorrow at 10:00 AM</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Bell className="h-4 w-4" />
                        <span className="text-sm">Reminder set</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upcoming Meeting - Bottom Right */}
                <div className="absolute bottom-4 right-0 w-[320px]">
                  <div className="bg-blue-600 rounded-2xl shadow-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white text-lg">Upcoming Meeting</h4>
                        <p className="text-white/90">Your appointment is in 1 hour</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Features Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background -z-10"></div>
        
        <Container>
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-500 to-blue-600">
                Effortless Scheduling
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to transform your booking experience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Enhanced Feature Cards */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-purple-500/30 rounded-2xl blur transition-all duration-300 group-hover:scale-105"></div>
              <div className="relative backdrop-blur-xl bg-white/10 p-8 rounded-2xl border border-white/20 space-y-6 h-full transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center mx-auto shadow-xl">
                  <Calendar className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-center">Create Your Schedule</h3>
                <p className="text-muted-foreground text-center">Define your availability with our intuitive interface. Set your hours, break times, and meeting durations effortlessly.</p>
              </div>
            </div>
            
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-primary/30 rounded-2xl blur transition-all duration-300 group-hover:scale-105"></div>
              <div className="relative backdrop-blur-xl bg-white/10 p-8 rounded-2xl border border-white/20 space-y-6 h-full transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-primary flex items-center justify-center mx-auto shadow-xl">
                  <Share2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-center">Share Your Link</h3>
                <p className="text-muted-foreground text-center">Distribute your personalized booking link across any platform. One link for all your scheduling needs.</p>
              </div>
            </div>
            
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 to-primary/30 rounded-2xl blur transition-all duration-300 group-hover:scale-105"></div>
              <div className="relative backdrop-blur-xl bg-white/10 p-8 rounded-2xl border border-white/20 space-y-6 h-full transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center mx-auto shadow-xl">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-center">Get Booked</h3>
                <p className="text-muted-foreground text-center">Watch as appointments flow in automatically. No more email chains or scheduling conflicts.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Stats Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-accent/5 to-background -z-10"></div>
        
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border border-white/20 text-center transform transition-all duration-300 hover:scale-105">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-4xl font-bold mb-2">10,000+</h4>
              <p className="text-muted-foreground">Active Users</p>
            </div>
            
            <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border border-white/20 text-center transform transition-all duration-300 hover:scale-105">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-primary flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-4xl font-bold mb-2">50,000+</h4>
              <p className="text-muted-foreground">Bookings Made</p>
            </div>
            
            <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border border-white/20 text-center transform transition-all duration-300 hover:scale-105">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Star className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-4xl font-bold mb-2">4.9/5</h4>
              <p className="text-muted-foreground">User Rating</p>
            </div>
          </div>
          
          {/* Enhanced CTA Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-purple-500/30 to-blue-600/30 rounded-3xl blur-2xl"></div>
            <div className="relative backdrop-blur-xl bg-white/10 rounded-3xl p-12 text-center border border-white/20">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-500 to-blue-600">
                  Ready to Transform Your Scheduling?
                </span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Join thousands of professionals who've elevated their scheduling experience.
              </p>
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-primary via-purple-500 to-blue-600 hover:opacity-90 transition-all duration-300 shadow-xl hover:shadow-primary/50 text-white">
                  Get Started for Free
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </LandingLayout>
  );
};

export default Home;
